import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import CreateDriver from './CreateDriver';
import { formatPhone } from '../utils/formatters';

const PAGE_SIZE = 10;

export default function ManageDrivers({ user, operatorDetails, vendorOnly = false }) {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [editDriver, setEditDriver] = useState(null);
    const [toast, setToast] = useState(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchDrivers = () => {
        setLoading(true);
        const url = user?.userType === 'operator'
            ? `/api/drivers/${user.operatorId}`
            : `/api/drivers/all`;

        fetch(url)
            .then(r => r.json())
            .then(data => {
                setDrivers(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Name': 'John Doe',
                'Email': 'john@example.com',
                'Phone Number': '501234567',
                'Country Code': '+971',
                'Alt Phone': '509876543',
                'Driver Code': 'D001',
                'Nationality': 'Indian',
                'Civil ID': '784-1234-1234567-1',
                'Civil ID Expiry': '2025-12-31',
                'Passport Number': 'L1234567',
                'Passport Expiry': '2028-10-10',
                'License Number': '123456',
                'License Expiry': '2026-05-20',
                'Onboard Date': '2023-01-15',
                'Status': 'Active',
                'Probation Period': '6 Months',
                'Accommodation Location': 'Dubai Silicon Oasis',
                'Base Salary': 3000,
                'Fixed Allowances': 500,
                'Overtime Rate': 15,
                'Uniform Cost': 100,
                'Accommodation Cost': 800,
                'Other Deduction': 50
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Drivers');
        XLSX.writeFile(wb, 'Drivers_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    name: row['Name'],
                    email: row['Email'],
                    phoneNumber: row['Phone Number'],
                    countryCode: row['Country Code'] || '+971',
                    altPhone: row['Alt Phone'],
                    driverCode: row['Driver Code'],
                    nationality: row['Nationality'],
                    civilId: row['Civil ID'],
                    civilIdExpiry: row['Civil ID Expiry'],
                    passportNumber: row['Passport Number'],
                    passportExpiry: row['Passport Expiry'],
                    licenseNumber: row['License Number'],
                    licenseExpiry: row['License Expiry'],
                    onboardDate: row['Onboard Date'],
                    status: row['Status'] || 'Active',
                    probationPeriod: row['Probation Period'],
                    accommodationLocation: row['Accommodation Location'],
                    baseSalary: row['Base Salary'] || 0,
                    fixedAllowances: row['Fixed Allowances'] || 0,
                    overtimeRate: row['Overtime Rate'] || 0,
                    uniformCost: row['Uniform Cost'] || 0,
                    accommodationCost: row['Accommodation Cost'] || 0,
                    otherDeduction: row['Other Deduction'] || 0
                }));

                const res = await fetch('/api/drivers/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operatorId: user?.operatorId, drivers: mapped })
                });
                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} drivers imported!`);
                    fetchDrivers();
                } else {
                    showToast(result.message || 'Import failed', 'error');
                }
            } catch (err) {
                showToast('Format error', 'error');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        fetchDrivers();
    }, [user]);

    const filtered = useMemo(() => {
        if (!Array.isArray(drivers)) return [];
        const q = search.toLowerCase();
        let list = drivers;

        if (vendorOnly) {
            list = list.filter(d => d.vendorId);
        } else {
            // "Drivers" menu shows ONLY direct drivers (no vendor assigned)
            list = list.filter(d => !d.vendorId);
        }

        return list.filter(d =>
            (d.name || '').toLowerCase().includes(q) ||
            (d.email || '').toLowerCase().includes(q) ||
            (d.phoneNumber || '').includes(q) ||
            (d.idNumber || '').toLowerCase().includes(q)
        );
    }, [drivers, search, vendorOnly, user]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleDelete = async (driver) => {
        if (!window.confirm(`Delete driver "${driver.name}"?`)) return;
        try {
            const res = await fetch(`/api/drivers/${driver._id}`, { method: 'DELETE' });
            if (res.ok) {
                showToast('Driver deleted successfully');
                fetchDrivers();
            } else {
                showToast('Failed to delete driver', 'error');
            }
        } catch (err) {
            showToast('Server error', 'error');
        }
    };

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        plus: 'M12 5v14 M5 12h14',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
        file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6',
        user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
        check: 'M20 6L9 17l-5-5',
        clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        alert: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 17c-.77 1.333.192 3 1.732 3z',
        upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
        download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    };

    const stats = useMemo(() => {
        return {
            total: filtered.length,
            active: filtered.filter(d => d.status === 'Active').length,
            onLeave: filtered.filter(d => d.status === 'On Leave').length,
            inactive: filtered.filter(d => d.status === 'Inactive').length
        };
    }, [filtered]);

    if (showCreate || editDriver) {
        return (
            <div className="manage-drivers-page-form-active">
                <style>{`
                    .manage-drivers-page-form-active { background: #f8fafc; min-height: 100vh; }
                `}</style>
                <CreateDriver
                    user={user}
                    editDriver={editDriver}
                    vendorOnly={vendorOnly}
                    operatorDetails={operatorDetails}
                    onBack={() => { setShowCreate(false); setEditDriver(null); }}
                    onSaved={() => { setShowCreate(false); setEditDriver(null); fetchDrivers(); }}
                />
            </div>
        );
    }

    return (
        <div className="manage-drivers-page">
            <style>{`
                .manage-drivers-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .manage-drivers-page { background: #0f172a; }

                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .theme-dark .title { color: #f1f5f9; }

                .add-btn { 
                    background: #312e81; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s;
                }
                .add-btn:hover { background: #1e1b4b; transform: translateY(-1px); }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                }
                .btn-primary {
                    background: #1e1b4b;
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 24px; }
                .stat-card {
                    background: white; border-radius: 16px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.25s;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .theme-dark .stat-card { background: #1e293b; border-color: #334155; }
                .stat-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08); }
                .stat-icon-wrap {
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
                .stat-val { font-size: 24px; font-weight: 800; color: #1e293b; line-height: 1; }
                .theme-dark .stat-val { color: #f1f5f9; }

                .toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .theme-dark .toolbar { background: #1e293b; border-color: #334155; }

                .search-box { position: relative; width: 300px; }
                .search-box input { 
                    width: 100%; padding: 10px 12px 10px 40px; border: 1px solid #e2e8f0; border-radius: 10px; 
                    font-size: 14px; outline: none; transition: all 0.2s;
                    background: #f8fafc;
                }
                .theme-dark .search-box input { background: #0f172a; border-color: #334155; color: white; }
                .search-box input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; }

                .table-container { 
                    background: white; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .theme-dark .table-container { background: #1e293b; border-color: #334155; }

                .drivers-table { width: 100%; border-collapse: collapse; }
                .drivers-table th { 
                    background: #f8fafc; padding: 16px 20px; text-align: left; font-size: 12px; 
                    font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0;
                }
                .theme-dark .drivers-table th { background: #1a2233; color: #94a3b8; border-bottom-color: #334155; }

                .drivers-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                .theme-dark .drivers-table td { border-bottom-color: #334155; color: #cbd5e1; }
                .drivers-table tr:last-child td { border-bottom: none; }
                .drivers-table tr:hover { background: #fdfdff; }
                .theme-dark .drivers-table tr:hover { background: #242f45; }

                .driver-info { display: flex; align-items: center; gap: 12px; }
                .avatar { 
                    width: 36px; height: 36px; background: #e0e7ff; color: #4338ca; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px;
                }
                .driver-name { font-weight: 600; color: #1e293b; }
                .theme-dark .driver-name { color: #f1f5f9; }

                .status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                }
                .status-active { background: #ecfdf5; color: #059669; }
                .status-under-review { background: #fef9c3; color: #92400e; }
                .status-inactive { background: #fef2f2; color: #dc2626; }
                .status-on-leave { background: #fffbeb; color: #d97706; }
                .status-resigned { background: #f3f4f6; color: #4b5563; }
                .status-terminated { background: #450a0a; color: #fecaca; }

                .actions { display: flex; gap: 10px; }
                .act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #64748b; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #6366f1; }
                .act-btn.delete:hover { background: #fee2e2; color: #ef4444; }
                .theme-dark .act-btn:hover { background: #334155; }

                .doc-link { color: #6366f1; display: flex; align-items: center; gap: 4px; text-decoration: none; font-weight: 500; }
                .doc-link:hover { text-decoration: underline; }

                .pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; }
                .page-info { font-size: 14px; color: #64748b; }
                .page-btns { display: flex; gap: 6px; }
                .p-btn { 
                    padding: 8px 14px; border: 1px solid #e2e8f0; background: white; border-radius: 8px;
                    font-size: 14px; cursor: pointer; transition: all 0.2s; color: #1e293b;
                }
                .p-btn:hover:not(:disabled) { border-color: #6366f1; color: #6366f1; }
                .p-btn.active { background: #312e81; color: white; border-color: #312e81; }
                .p-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .theme-dark .p-btn { background: #1e293b; border-color: #334155; color: #94a3b8; }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    background: #1e293b; color: white; z-index: 1000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <h1 className="title">{vendorOnly ? 'Outsourced Vendor Drivers' : 'Direct Fleet Drivers'}</h1>
                <div style={{ display: 'flex', gap: 12 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    {!vendorOnly && (
                        <>
                            <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                                <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                                {importing ? 'Importing...' : 'Import'}
                            </button>
                            <button className="premium-btn btn-template" onClick={downloadTemplate}>
                                <Icon d={icons.download} size={18} strokeWidth={2.5} />
                                Template
                            </button>
                        </>
                    )}
                    <button className="premium-btn btn-primary" onClick={() => setShowCreate(true)} style={vendorOnly ? { background: '#10b981' } : { background: '#4338ca' }}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        {vendorOnly ? 'Register Outsourced Driver' : 'Register Direct Driver'}
                    </button>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #10b981' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: vendorOnly ? '#ecfdf5' : '#eef2ff' }}>
                        <Icon d={icons.user} color={vendorOnly ? '#059669' : '#4338ca'} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total {vendorOnly ? 'Resources' : 'Drivers'}</span>
                        <span className="stat-val">{stats.total}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #10b981' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Active</span>
                        <span className="stat-val">{stats.active}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #f59e0b' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">On Leave</span>
                        <span className="stat-val">{stats.onLeave}</span>
                    </div>
                </div>
                <div className="stat-card" style={vendorOnly ? { borderLeft: '4px solid #ef4444' } : {}}>
                    <div className="stat-icon-wrap" style={{ background: '#fef2f2' }}>
                        <Icon d={icons.alert} color="#dc2626" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Inactive</span>
                        <span className="stat-val">{stats.inactive}</span>
                    </div>
                </div>
            </div>

            <div className="toolbar">
                <div className="search-box">
                    <span className="search-icon"><Icon d={icons.search} size={18} /></span>
                    <input
                        type="text"
                        placeholder="Search drivers by name, email or ID..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>
                <div className="filters">
                    {/* Add more filters if needed */}
                </div>
            </div>

            <div className="table-container">
                <table className="drivers-table">
                    <thead>
                        <tr>
                            <th>Driver</th>
                            {user?.userType === 'admin' && <th>Operator</th>}
                            {vendorOnly && <th>Vendor</th>}
                            <th>Contact Info</th>
                            <th>ID Number</th>
                            <th>Document</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={vendorOnly ? "8" : "7"} style={{ textAlign: 'center', padding: '60px' }}>Loading drivers...</td></tr>
                        ) : pageData.length === 0 ? (
                            <tr><td colSpan={vendorOnly ? "8" : "7"} style={{ textAlign: 'center', padding: '60px' }}>No drivers found.</td></tr>
                        ) : (
                            pageData.map(driver => (
                                <tr key={driver._id}>
                                    <td>
                                        <div className="driver-info">
                                            <div className="avatar">{driver.name?.charAt(0)}</div>
                                            <div className="driver-name">{driver.name}</div>
                                        </div>
                                    </td>
                                    {user?.userType === 'admin' && (
                                        <td style={{ fontWeight: 600, color: '#4f46e5' }}>
                                            {driver.operatorId?.companyName || 'N/A'}
                                        </td>
                                    )}
                                    {vendorOnly && (
                                        <td style={{ fontWeight: 600, color: '#10b981' }}>
                                            {driver.vendorId?.vendorName || (typeof driver.vendorId === 'string' ? 'Loading...' : 'N/A')}
                                        </td>
                                    )}
                                    <td>
                                        <div style={{ fontWeight: 500, color: '#1e293b' }}>
                                            {formatPhone(driver.phoneNumber, operatorDetails?.country)}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: 13 }}>{driver.email}</div>
                                    </td>
                                    <td style={{ fontWeight: 500 }}>{driver.civilId || driver.idNumber || '-'}</td>
                                    <td>
                                        {driver.document ? (
                                            <a href={`/api/uploads/${driver.document}`} target="_blank" rel="noreferrer" className="doc-link">
                                                <Icon d={icons.file} size={14} />
                                                View Document
                                            </a>
                                        ) : (
                                            <span style={{ color: '#94a3b8' }}>No Document</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`status-badge status-${driver.status?.toLowerCase().replace(' ', '-') || 'inactive'}`}>
                                            {driver.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions">
                                            <button className="act-btn" title="Edit" onClick={() => setEditDriver(driver)}>
                                                <Icon d={icons.edit} size={16} />
                                            </button>
                                            <button className="act-btn delete" title="Delete" onClick={() => handleDelete(driver)}>
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {!loading && filtered.length > PAGE_SIZE && (
                <div className="pagination">
                    <div className="page-info">
                        Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(filtered.length, page * PAGE_SIZE)} of {filtered.length} drivers
                    </div>
                    <div className="page-btns">
                        <button className="p-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`p-btn ${page === p ? 'active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                        <button className="p-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
